import './index-CilXBR1U.js';
import { A as A11y, N as Navigation, P as Pagination, S as Scrollbar, a as Autoplay, b as Swiper } from './effect-cards-AVf2se9P.js';

/**
 * SwiperSlider
 * @description Web component used to create sliders, configurable with data-options attribute.
 * @documentation https://swiperjs.com/swiper-api
 * @example
<swiper-slider>
  <div
    class="swiper"
    data-swiper
    data-options='{
      "threshold": 10,
      "loop": false,
      "scrollbar": true,
      "navigation": {
        "nextEl": ".swiper-button-next-{{ section.id }}",
        "prevEl": ".swiper-button-prev-{{ section.id }}"
      },
      "pagination": {
        "el": ".swiper-pagination-{{ section.id }}",
        "type": "bullets"
      },
      "slidesPerView": 1,
      "spaceBetween": 16,
      "breakpoints": {
        "768": {
          "slidesPerView": 2,
          "spaceBetween": 24
        },
        "1024": {
          "slidesPerView": 3,
          "spaceBetween": 32
        }
      }
    }'
  >
    <div class="swiper-wrapper">
      {%- for item in (1..8) -%}
        <div class="swiper-slide" data-swiper-slide-index="{{ forloop.index }}">
          <h2>
            Your slide
          </h2>
        </div>
      {%- endfor -%}
    </div>

    <button
      class="swiper-button-prev swiper-button-prev-{{ section.id }} "
      aria-label="{% render "format-translation", namespace: "accessibility", key: "previous_slide", fallback: "Previous slide" %}"
    >
      {%- render 'global-icon', icon: 'chevron-left', icon_class: 'w-5' -%}
    </button>
    <button
      class="swiper-button-next swiper-button-next-{{ section.id }} "
      aria-label="{% render "format-translation", namespace: "accessibility", key: "next_slide", fallback: "Next slide" %}"
    >
      {%- render 'global-icon', icon: 'chevron-right', icon_class: 'w-5' -%}
    </button>

    <div class="swiper-pagination swiper-pagination-{{ section.id }} flex justify-center"></div>
    <div class="swiper-scrollbar"></div>
  </div>
</swiper-slider>
 */


if (!customElements.get('swiper-slider')) {
  class SwiperSlider extends HTMLElement {
    constructor() {
      super();
      this.swiper = this.querySelector('[data-swiper]');

      // Stop when the swiper is not found
      if (!this.swiper) {
        return;
      }

      // Default to improve user experience
      this.swiperOptions = {
        modules: [A11y, Navigation, Pagination, Scrollbar, Autoplay],
        threshold: 10,
      };

      // Check if we have extra options on the HTML
      if (this.swiper.dataset.options) {
        const options = JSON.parse(this.swiper.dataset.options);
        if (options) {
          this.swiperOptions = {
            ...this.swiperOptions,
            ...options,
          };
        }
      }

      // Call swiper with selected swiper element and options
      this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);

      /**
       * Listen to extra events when in Shopify editor
       */
      if (Shopify.designMode) {
        // Update the swiper when the section event is triggerd
        window.addEventListener('shopify:section:load', (event) => {
          this.swiperInstance.update();
          if (this.swiperOptions.navigation) {
            this.swiperInstance.navigation.init();
            this.swiperInstance.navigation.update();
          }
        });

        // When on block select go to the slide in front-end
        window.addEventListener('shopify:block:select', (event) => {
          this.swiperInstance.update();
          if (this.swiperOptions.navigation) {
            this.swiperInstance.navigation.init();
            this.swiperInstance.navigation.update();
          }
          this.handleBlockSelect(event);
        });

        // When on block deselect go to the slide in front-end
        window.addEventListener('shopify:block:deselect', (event) => {
          this.swiperInstance.update();
          if (this.swiperOptions.navigation) {
            this.swiperInstance.navigation.init();
            this.swiperInstance.navigation.update();
          }
          this.handleBlockSelect(event);
        });
      }
    }

    /**
     * Handles the theme editor block change/edit event
     * @param {Object} event
     */
    handleBlockSelect(event) {
      // Check if the slide index is set
      if (!('swiperSlideIndex' in event.target.dataset)) {
        return;
      }

      // Set the slide index based on loop settings or not
      let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
      if (this.swiperOptions.loop) {
        swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;
      }

      // Slide to slide based on the data attribute from the target
      const sliderToUpdate = event.target.closest('[data-swiper]');
      sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 1000);
    }
  }

  window.SwiperSlider = SwiperSlider;

  customElements.define('swiper-slider', SwiperSlider);
}
